/**
 * InstitutionalModeContext.jsx - Institutional Mode State Management
 * 
 * FOUNDER DIRECTIVE: Institutional Mode Toggle
 * 
 * When INSTITUTIONAL MODE is active:
 * - Hide tutorial hints
 * - Hide demo labels (e.g., "DEMO NETWORK DATA")
 * - Hide explanatory onboarding text
 * - Hide demo network indicators
 * 
 * Preserves:
 * - Atlas visualization
 * - Registry Explorer
 * - Simulation engine
 * - Forecast layers
 * - All core functionality
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// Context
const InstitutionalModeContext = createContext(null);

// Provider Component
export function InstitutionalModeProvider({ children }) {
  // Load initial state from localStorage
  const [isInstitutionalMode, setIsInstitutionalMode] = useState(() => {
    try {
      const stored = localStorage.getItem('granullo_institutional_mode');
      return stored === 'true';
    } catch {
      return false;
    }
  });

  // Toggle function
  const toggleMode = useCallback(() => {
    setIsInstitutionalMode(prev => {
      const newValue = !prev;
      try {
        localStorage.setItem('granullo_institutional_mode', String(newValue));
      } catch {}
      return newValue;
    });
  }, []);

  // Set specific mode
  const setMode = useCallback((value) => {
    setIsInstitutionalMode(value);
    try {
      localStorage.setItem('granullo_institutional_mode', String(value));
    } catch {}
  }, []);

  // Keyboard shortcut: Ctrl/Cmd + Shift + I to toggle
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        toggleMode();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleMode]);

  const value = {
    // State
    isInstitutionalMode,
    isDemoMode: !isInstitutionalMode,
    
    // Actions
    toggleMode,
    setMode,
    enableInstitutionalMode: () => setMode(true),
    enableDemoMode: () => setMode(false),
  };

  return (
    <InstitutionalModeContext.Provider value={value}>
      {children}
    </InstitutionalModeContext.Provider>
  );
}

// Hook to use institutional mode
export function useInstitutionalMode() {
  const context = useContext(InstitutionalModeContext);
  if (!context) {
    throw new Error('useInstitutionalMode must be used within an InstitutionalModeProvider');
  }
  return context;
}

export default InstitutionalModeContext;
